import React, { useState, useEffect } from 'react';
import saasAdminApi from '../services/saasAdminApi';
import { formatApiError } from '../utils/errorMessage';
import { todayLocal, localDateKey } from '../utils/localDate';
import { parseMoneyInput, MONEY_FIELD_ERROR } from '../utils/money';

function daysFromTodayLocal(days) {
  const date = new Date();
  date.setDate(date.getDate() + days);
  return localDateKey(date);
}

/**
 * SubscriptionModal Component
 * Single Responsibility: Manage subscription for a single business
 */
const SubscriptionModal = ({ business, isOpen, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    plan_name: 'standard',
    monthly_price: 0.00,
    billing_cycle: 'monthly',
    status: 'active',
    current_period_start: todayLocal(),
    current_period_end: daysFromTodayLocal(30),
    trial_ends_at: null
  });

  useEffect(() => {
    if (business?.subscription_id) {
      // Load existing subscription
      loadSubscription();
    } else {
      // Reset form for new subscription
      resetForm();
    }
  }, [business, isOpen]);

  const loadSubscription = async () => {
    if (!business?.subscription_id) return;
    
    try {
      setLoading(true);
      const res = await saasAdminApi.getSubscription(business.subscription_id);
      const sub = res.data;
      setForm({
        plan_name: sub.plan_name || 'standard',
        monthly_price: sub.monthly_price || 0.00,
        billing_cycle: sub.billing_cycle || 'monthly',
        status: sub.status || 'active',
        current_period_start: sub.current_period_start || todayLocal(),
        current_period_end: sub.current_period_end || daysFromTodayLocal(30),
        trial_ends_at: sub.trial_ends_at ? new Date(sub.trial_ends_at).toISOString().split('T')[0] : null
      });
    } catch (e) {
      setError(formatApiError(e, 'Failed to load subscription'));
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setForm({
      plan_name: 'standard',
      monthly_price: 0.00,
      billing_cycle: 'monthly',
      status: 'active',
      current_period_start: todayLocal(),
      current_period_end: daysFromTodayLocal(30),
      trial_ends_at: null
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!business) return;
    const monthlyPrice = parseMoneyInput(form.monthly_price);
    if (monthlyPrice.error || monthlyPrice.value === null) {
      setError(`Monthly price: ${MONEY_FIELD_ERROR}`);
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      await saasAdminApi.createSubscription({
        business_id: business.id,
        ...form,
        monthly_price: monthlyPrice.value,
        trial_ends_at: form.trial_ends_at || null
      });

      if (onSuccess) onSuccess();
      onClose();
    } catch (e) {
      setError(formatApiError(e, 'Failed to save subscription'));
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async () => {
    if (!business?.subscription_id) {
      onClose();
      return;
    }

    if (!window.confirm('Are you sure you want to cancel this subscription?')) {
      return;
    }

    try {
      setLoading(true);
      await saasAdminApi.cancelSubscription(business.subscription_id, {
        cancel_at_period_end: true,
        cancellation_reason: 'Cancelled by admin'
      });
      if (onSuccess) onSuccess();
      onClose();
    } catch (e) {
      setError(formatApiError(e, 'Failed to cancel subscription'));
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !business) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-semibold">
              Manage Subscription - {business.name}
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              ✕
            </button>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 text-red-700 border border-red-200 rounded" role="alert">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Plan Name
                </label>
                <select
                  value={form.plan_name}
                  onChange={(e) => setForm({ ...form, plan_name: e.target.value })}
                  className="w-full border rounded px-3 py-2"
                  required
                >
                  <option value="free">Free</option>
                  <option value="trial">Trial</option>
                  <option value="standard">Standard</option>
                  <option value="premium">Premium</option>
                  <option value="enterprise">Enterprise</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Monthly Price ($)
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.monthly_price}
                  onChange={(e) => setForm({ ...form, monthly_price: e.target.value })}
                  className="w-full border rounded px-3 py-2"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Billing Cycle
                </label>
                <select
                  value={form.billing_cycle}
                  onChange={(e) => setForm({ ...form, billing_cycle: e.target.value })}
                  className="w-full border rounded px-3 py-2"
                  required
                >
                  <option value="monthly">Monthly</option>
                  <option value="yearly">Yearly</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <select
                  value={form.status}
                  onChange={(e) => setForm({ ...form, status: e.target.value })}
                  className="w-full border rounded px-3 py-2"
                  required
                >
                  <option value="trial">Trial</option>
                  <option value="active">Active</option>
                  <option value="suspended">Suspended</option>
                  <option value="cancelled">Cancelled</option>
                  <option value="expired">Expired</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Period Start
                </label>
                <input
                  type="date"
                  value={form.current_period_start}
                  onChange={(e) => setForm({ ...form, current_period_start: e.target.value })}
                  className="w-full border rounded px-3 py-2"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Period End
                </label>
                <input
                  type="date"
                  value={form.current_period_end}
                  onChange={(e) => setForm({ ...form, current_period_end: e.target.value })}
                  className="w-full border rounded px-3 py-2"
                  required
                />
              </div>

              {form.status === 'trial' && (
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Trial Ends At
                  </label>
                  <input
                    type="date"
                    value={form.trial_ends_at || ''}
                    onChange={(e) => setForm({ ...form, trial_ends_at: e.target.value || null })}
                    className="w-full border rounded px-3 py-2"
                  />
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t">
              {business.subscription_id && (
                <button
                  type="button"
                  onClick={handleCancel}
                  disabled={loading}
                  className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:bg-red-300"
                >
                  Cancel Subscription
                </button>
              )}
              <button
                type="button"
                onClick={onClose}
                disabled={loading}
                className="px-4 py-2 border rounded hover:bg-gray-50 disabled:opacity-50"
              >
                Close
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-blue-300"
              >
                {loading ? 'Saving...' : 'Save Subscription'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionModal;

